import React, { useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Tooltip,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import LinkIcon from "@mui/icons-material/Link";
import { formatInTimeZone } from "date-fns-tz";

const dateView = (session, event) => {
  const { timezone } = event;
  const { startsAt } = session;
  const datePattern = "MMM do";
  const timePattern = "hh:mm a";
  let dateText;
  let timeText;
  if (startsAt) {
    dateText = formatInTimeZone(startsAt.toDate(), timezone, datePattern);
    timeText = formatInTimeZone(startsAt.toDate(), timezone, timePattern);
  } else {
    dateText = "";
  }

  return (
    <Box>
      <Typography>{dateText}</Typography>
      <Typography>{timeText}</Typography>
    </Box>
  );
};

const titleView = (session, event) => {
  const { name, address: sessionAddress, addressSelection } = session || {};
  const { address: eventAddress } = event;
  const address =
    addressSelection === "useEvent" ? eventAddress : sessionAddress;
  const { placeName } = address || {};

  return (
    <Box>
      <Typography variant="" paddingTop={"0px"}>
        {name}
      </Typography>
      <Typography color={"primary.lightText"}>{placeName}</Typography>
    </Box>
  );
};

const ViewSessionButton = ({ session, onShowCopyToast }) => {
  if (!session || !session.publicUrl) {
    return null;
  }

  const { publicUrl } = session;

  return (
    <>
      <Tooltip title="View public session page">
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            window.open(publicUrl, "_blank", "noreferrer");
          }}
        >
          <LaunchIcon color="primary" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Copy public URL">
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            navigator.clipboard.writeText(publicUrl);
            onShowCopyToast();
          }}
        >
          <LinkIcon color="primary" />
        </IconButton>
      </Tooltip>
    </>
  );
};

const attendeesView = (session, event, onShowCopyToast) => {
  const {
    totalTickets,
    ticketTypes: sessionTicketTypes,
    ticketTypeSelection,
    totalTicketsCheckedIn,
    ticketTypeStats,
  } = session;
  const { ticketTypes } = event;
  const filteredTicketTypes =
    ticketTypeSelection === "all"
      ? ticketTypes
      : ticketTypes.filter(({ key }) => sessionTicketTypes.includes(key));
  return (
    <Box
      sx={{
        textAlign: "right",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: "10px",
      }}
    >
      <ViewSessionButton session={session} onShowCopyToast={onShowCopyToast} />
      <Box>
        <Typography>{`Checked In: ${totalTicketsCheckedIn}/${totalTickets}`}</Typography>
        {filteredTicketTypes.map((ticketType) => {
          const { name, key: ticketTypeId } = ticketType;
          const stats = ticketTypeStats?.[ticketTypeId];
          if (!stats) {
            return null;
          }
          const {
            totalTickets: typeTotalTickets,
            totalTicketsCheckedIn: typeTotalTicketsCheckedIn,
          } = stats;
          return (
            <Typography
              key={`ticket-type-stats-${ticketTypeId}`}
            >{`${name}: ${typeTotalTicketsCheckedIn}/${typeTotalTickets}`}</Typography>
          );
        })}
      </Box>
    </Box>
  );
};

const SessionsList = () => {
  const { event } = useOutletContext();
  const { sessions } = event;
  const [showCopySnackbar, setShowCopySnackbar] = useState(false);

  const onShowCopyToast = () => {
    setShowCopySnackbar(true);
  };

  return (
    <Box>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={showCopySnackbar}
        autoHideDuration={2000}
        onClose={async () => {
          await setShowCopySnackbar(false);
        }}
      >
        <Alert
          onClose={async () => {
            await setShowCopySnackbar(false);
          }}
          severity="success"
        >
          URL Copied!
        </Alert>
      </Snackbar>
      <Box
        width={"100%"}
        display={"flex"}
        flexDirection={{ xs: "column", md: "row" }}
        alignItems={{ xs: "left", md: "center" }}
        gap={"5px"}
        sx={{ marginTop: "20px" }}
      >
        <Typography variant="h2" flexGrow={1}>
          Sessions
        </Typography>
        <Button component={Link} to={"new"} sx={{ width: "150px" }}>
          New Session
        </Button>
      </Box>
      <Box
        display={"flex"}
        flexDirection={"column"}
        gap={"20px"}
        sx={{ marginTop: "20px" }}
      >
        {(sessions || []).map((session) => {
          const { id } = session;
          return (
            <Box
              key={`session-${id}`}
              component={Link}
              to={id}
              sx={{ textDecoration: "none" }}
            >
              <Paper elevation={5}>
                <Grid
                  container
                  sx={{ cursor: "pointer" }}
                  spacing={1}
                  padding="20px"
                >
                  <Grid item xs={12} sm={12} md={1}>
                    {dateView(session, event)}
                  </Grid>

                  <Grid item xs={12} sm={12} md={6}>
                    {titleView(session, event)}
                  </Grid>
                  <Grid item xs={12} sm={12} md={5}>
                    {attendeesView(session, event, onShowCopyToast)}
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SessionsList;
